import React, { useCallback, useMemo, useState } from 'react';
import {
  AsyncConnector,
  DataGrid,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Input,
  Layout,
  LayoutItem,
  PluralizeUtil,
  Suspense,
  useApi,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateReputationDetailsRequestBody,
  CandidateReputationDetails,
  CandidateReputationDetailsResponseBody,
  Opportunity,
  User,
} from '@axiom/validation';
import {
  CandidateReputationActions,
  CandidateReputationDetailsSort,
} from '@axiom/const';

import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { OpportunityApi } from '../../api/opportunity-api';
import { UserActivityApi } from '../../api/user-activity-api';
import { TopTasksApi } from '../../api/top-tasks-api';
import { ActionReputationApi } from '../../api/action-reputation-api';
import type { ReputationSearch } from '../../schema/reputation-search-form-schema';
import { ReputationSearchFormSchema } from '../../schema/reputation-search-form-schema';
import { ReputationHistoryModal } from '../ReputationHistoryModal/ReputationHistoryModal';

import { ReputationDetailsDismissModal } from './ReputationDetailsDismissModal';
import { ReputationDetailsDisputeModal } from './ReputationDetailsDisputeModal';
import { ReputationDetailsFeedbackModal } from './ReputationDetailsFeedbackModal';
import { ReputationDetailsUtil } from './ReputationDetailsUtil';

type SortableColumn =
  | 'status'
  | 'responseDate'
  | 'candidateName'
  | 'accountName'
  | 'meetsExpectations'
  | 'inTopTenPercent'
  | 'respondentName'
  | 'engagementName'
  | 'accountOwner'
  | 'matterId';

const columnToSortField: Record<
  SortableColumn,
  (typeof CandidateReputationDetailsSort)[keyof typeof CandidateReputationDetailsSort]
> = {
  status: CandidateReputationDetailsSort.Status,
  responseDate: CandidateReputationDetailsSort.ResponseDate,
  candidateName: CandidateReputationDetailsSort.TalentName,
  accountName: CandidateReputationDetailsSort.AccountName,
  meetsExpectations: CandidateReputationDetailsSort.MeetsExpectations,
  inTopTenPercent: CandidateReputationDetailsSort.TopTenPercent,
  respondentName: CandidateReputationDetailsSort.RespondentName,
  engagementName: CandidateReputationDetailsSort.EngagementName,
  accountOwner: CandidateReputationDetailsSort.AccountOwner,
  matterId: CandidateReputationDetailsSort.MatterId,
};

export type ReputationDetailsProps = {
  name?: string;
  candidateId?: Candidate['id'];
  userId?: User['id'];
  opportunityId?: Opportunity['id'];
  withSearch?: boolean;
  withCount?: boolean;
  viewedIn: 'opportunity-detail' | 'talent-detail' | null;
};

export const ReputationDetails = ({
  name = 'REPUTATION_DETAILS',
  candidateId,
  userId,
  opportunityId,
  withSearch = false,
  withCount = false,
  viewedIn,
}: ReputationDetailsProps) => {
  if (!candidateId && !opportunityId && !userId) {
    throw new Error(
      'candidateId, opportunityId, or userId is required depending on your needs'
    );
  }
  const [{ data: user }] = useApi(UserApi.readSessionUser());
  const [sendFeedBackData, setSendFeedBackData] =
    useState<CandidateReputationDetails | null>(null);
  const [disputeData, setDisputeData] =
    useState<CandidateReputationDetails | null>(null);
  const [dismissData, setDismissData] =
    useState<CandidateReputationDetails | null>(null);
  const [historyData, setHistoryData] =
    useState<CandidateReputationDetails | null>(null);
  const [resultCount, setResultCount] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  // Bumped after a context-menu action invalidates the cache. Folding it into
  // the GridConnector memo hands <DataGrid> a fresh dataSource reference, which
  // forces a re-fetch in BOTH display modes. The pagination grid
  // (viewedIn === 'opportunity-detail') has no onInvalidate subscription, so a
  // cache invalidation alone would otherwise never refresh it.
  const [refreshToken, setRefreshToken] = useState(0);

  const GridConnector = useMemo(
    () =>
      new AsyncConnector({
        getData: ({ sort, search = searchText || '', page }) => {
          const activeColumn = (Object.keys(sort).find(key => sort[key]) ||
            'responseDate') as SortableColumn;
          const sortField = columnToSortField[activeColumn];
          const order = sort[activeColumn] || 'desc';
          const body: CandidateReputationDetailsRequestBody = {
            sort: sortField,
            search,
            page,
            pageSize: 20,
            order: order === 'asc' ? 'asc' : 'desc',
          };

          if (candidateId) {
            return CandidateApi.readCandidateReputationDetails({
              candidateId,
              body,
            });
          } else if (opportunityId) {
            return OpportunityApi.readOpportunityReputationDetails({
              opportunityId,
              body,
            });
          }

          if (!userId) {
            throw new Error(
              'userId is required when candidateId is not provided'
            );
          }

          return UserActivityApi.readUserTalentReputationDetails({
            userId,
            body,
          });
        },
        parseData: (results: CandidateReputationDetailsResponseBody) => {
          setResultCount(results.meta.resultCount);
          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: results.data,
          };
        },
      }),
    [candidateId, userId, opportunityId, searchText, refreshToken]
  );

  const refreshData = async () => {
    if (candidateId) {
      await CandidateApi.refreshCandidateReputationDetails({ candidateId });
    }
    if (opportunityId) {
      await OpportunityApi.refreshOpportunityReputationDetails({
        opportunityId,
      });
    }
    if (userId) {
      await UserActivityApi.refreshUserTalentReputationDetails({ userId });
      await TopTasksApi.refreshTopTasks();
    }
    // Recreate the AsyncConnector so <DataGrid> receives a new dataSource and
    // re-fetches. Required for the pagination display mode, which (unlike the
    // infinite mode) does not subscribe to cache invalidation events.
    setRefreshToken(token => token + 1);
  };

  const handleModalSubmit = async ({
    clearModalState,
  }: {
    clearModalState: () => void;
  }) => {
    clearModalState();
    await ReputationDetailsUtil.runDelay();
    await refreshData();
  };

  const handleSentFeedbackOnClick = async (
    data: CandidateReputationDetails
  ) => {
    await ActionReputationApi.createActionReputationDetails({
      reputationId: data.id,
      body: {
        action: CandidateReputationActions.SHARED_LIVE,
        actionNotes: null,
      },
    });

    await ReputationDetailsUtil.runDelay();
    await refreshData();
  };

  // Stabilize the props passed to <DataGrid> so that re-renders triggered by
  // unrelated cache invalidations (e.g. CandidateApi.refreshCandidate from a
  // flag toggle in TalentProfileHeader) don't churn AgGridReact's columnDefs
  // and wipe the "Reputation details will appear here" no-rows overlay
  // (MIE-317).
  const getContextMenuItems = useCallback(
    (
      menuData: Parameters<
        typeof ReputationDetailsUtil.getContextMenuItems
      >[0]['params']
    ) =>
      ReputationDetailsUtil.getContextMenuItems({
        params: menuData,
        user,
        onClickSendFeedBack: setSendFeedBackData,
        onClickSentFeedBack: handleSentFeedbackOnClick,
        onClickDispute: setDisputeData,
        onClickDismiss: setDismissData,
      }),
    [user]
  );

  const gridColumns = useMemo(
    () =>
      ReputationDetailsUtil.buildGridColumns({
        candidateId,
        setHistoryData,
      }),
    [candidateId]
  );

  // Intentionally NOT memoised. Memoising this element makes React bail out of
  // re-renders, which stops <DataGrid>/AgGridReact from re-syncing after an
  // ancestor <Suspense> suspend/resume disrupts the grid — leaving it stuck on
  // its loading overlay (MIE-473). Re-rendering is safe because DataGrid's
  // columnDefs are now referentially stable, so re-renders no longer wipe the
  // "Reputation details will appear here" no-rows overlay (MIE-317).
  const dataGridElement = (
    <DataGrid
      name="CANDIDATE_REPUTATION_GRID"
      displayMode={
        viewedIn === 'opportunity-detail' ? 'pagination' : 'infinite'
      }
      dataSource={GridConnector}
      noResultsMessage="Reputation details will appear here"
      getContextMenuItems={getContextMenuItems}
      allowContextMenu
      simple
      tooltipShowDelay={250}
    >
      {gridColumns}
    </DataGrid>
  );

  return (
    <>
      <Layout stretched direction="vertical" name={name}>
        {withSearch ? (
          <LayoutItem bottomGutter="16px">
            <Form
              name="REPUTATION_SEARCH_FORM"
              schema={ReputationSearchFormSchema}
              initialValues={{ search: searchText }}
              submitOnChange
              onChange={(changedData: Partial<ReputationSearch>) => {
                setSearchText(changedData.search ?? null);
              }}
            >
              {() => {
                return (
                  <Grid name="SEARCH_FORM">
                    <GridRow stretched>
                      <GridColumn width={2}>
                        <Input
                          name="search"
                          icon="search"
                          placeholder="Search"
                        />
                      </GridColumn>
                    </GridRow>
                  </Grid>
                );
              }}
            </Form>
          </LayoutItem>
        ) : null}
        {withCount && resultCount !== null ? (
          <LayoutItem name="RESULT_COUNT_DISPLAY" bottomGutter="16px">
            {resultCount} survey{' '}
            {PluralizeUtil({ text: 'response', count: resultCount })}
          </LayoutItem>
        ) : null}
        <LayoutItem stretched>{dataGridElement}</LayoutItem>
      </Layout>
      {sendFeedBackData ? (
        <Suspense fallback={null}>
          <ReputationDetailsFeedbackModal
            onClose={() => {
              setSendFeedBackData(null);
            }}
            onSubmit={async () => {
              await handleModalSubmit({
                clearModalState: () => {
                  setSendFeedBackData(null);
                },
              });
            }}
            feedBackData={sendFeedBackData}
          />
        </Suspense>
      ) : null}
      {disputeData ? (
        <Suspense fallback={null}>
          <ReputationDetailsDisputeModal
            onClose={() => {
              setDisputeData(null);
            }}
            onSubmit={async () => {
              await handleModalSubmit({
                clearModalState: () => {
                  setDisputeData(null);
                },
              });
            }}
            disputeData={disputeData}
          />
        </Suspense>
      ) : null}
      {dismissData ? (
        <Suspense fallback={null}>
          <ReputationDetailsDismissModal
            onClose={() => {
              setDismissData(null);
            }}
            onSubmit={async () => {
              await handleModalSubmit({
                clearModalState: () => {
                  setDismissData(null);
                },
              });
            }}
            dismissData={dismissData}
          />
        </Suspense>
      ) : null}
      {historyData ? (
        <Suspense fallback={null}>
          <ReputationHistoryModal
            reputationId={historyData.id}
            onClose={() => {
              setHistoryData(null);
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
};
